import { Injectable, inject, NgZone } from '@angular/core';
import { RollbarService } from '@becksdevteam/rollbar-angular';
import flagsmith, { type IFlags } from '@flagsmith/flagsmith';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { type Action, Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import config from '../../app.config';
import { FlagsmithFeatureFlagActions } from '../actions';
import type { FlagsmithFeatureFlagEntity } from '../reducers';
import { selectFlagsmithFeatureFlagsLoaded } from '../selectors';

@Injectable()
export class FlagsmithFeatureFlagEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private rollbarService = inject(RollbarService);
  private ngZone = inject(NgZone);

  constructor() {
    this.ngZone.runOutsideAngular(() => {
      flagsmith.init({
        environmentID: config.flagsmithKey,
        realtime: true,
        enableLogs: false,
        onChange: (_oldFlags, params) => {
          this.ngZone.run(() => {
            if (params.flagsChanged) {
              this.store.dispatch(FlagsmithFeatureFlagActions.flagsChanged({ flagKeys: params.flagsChanged }));
            }
          });
        },
        onError: (err) => {
          this.rollbarService.log(err, {
            level: 'error',
            custom: { message: 'Flagsmith error' },
          });
        },
      });
    });
  }

  flagsmithFeatureFlagsChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FlagsmithFeatureFlagActions.flagsChanged),
      concatLatestFrom(() => this.store.select(selectFlagsmithFeatureFlagsLoaded)),
      switchMap(([{ flagKeys }, flagsLoaded]) => {
        const flags = flagsmith.getState().flags;
        if (flags) {
          const featureFlagEntities = this.flagsmithFeatureFlagsToEntities(flags);
          if (flagsLoaded) {
            const actions: Action[] = [];

            const changedFlags = featureFlagEntities.filter((flag) => flagKeys.includes(flag.name));
            if (changedFlags.length > 0) {
              actions.push(FlagsmithFeatureFlagActions.upsertFlags({ flags: changedFlags }));
            }

            const removedFlags = flagKeys.filter((flagKey) => !flags[flagKey]);
            if (removedFlags.length > 0) {
              actions.push(FlagsmithFeatureFlagActions.removeFlags({ flagKeys: removedFlags }));
            }

            return actions;
          }

          return of(FlagsmithFeatureFlagActions.addFlags({ flags: featureFlagEntities }));
        }

        return of(FlagsmithFeatureFlagActions.clearFlags());
      })
    )
  );

  private flagsmithFeatureFlagsToEntities(flags: IFlags<string>): FlagsmithFeatureFlagEntity[] {
    return Object.entries(flags).reduce<FlagsmithFeatureFlagEntity[]>((acc, [key, value]) => {
      acc.push({ name: key, ...value });
      return acc;
    }, []);
  }
}
